import React, { useCallback, useEffect, useMemo, useState } from 'react';
import collectionBoyService from '../../services/collectionBoyService';
import masterService from '../../services/masterService';
import MasterPageWrapper from './common/MasterPageWrapper';
import FormSection from './common/FormSection';
import ConfirmModal from './components/ConfirmModal';
import CommonPagination from '../../components/common/CommonPagination';
import usePagination from '../../hooks/usePagination';
import {
  EMPLOYMENT_TYPES, EMPTY_COLLECTION_BOY, GENDERS, ID_PROOF_TYPES, formFromRecord, toPayload, validateCollectionBoy,
} from '../../utils/collectionBoyForm';
import './InvestigationMasterPage.css';
import './CollectionBoyMasterPage.css';

// The label is tied to its control (htmlFor / id) so a screen reader announces it and a click on the label focuses the field.
const Field = ({ label, required, error, children, wide }) => {
  const id = `cb-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  return (
    <div className={`form-group ${wide ? 'col-span-2' : ''}`}>
      <label className="form-label" htmlFor={id}>{label} {required && <span className="form-req">*</span>}</label>
      {React.isValidElement(children) && children.type !== React.Fragment ? React.cloneElement(children, { id }) : children}
      {error && <span className="error-message" role="alert">{error}</span>}
    </div>
  );
};

export const CollectionBoyMasterPage = () => {
  const [viewMode, setViewMode] = useState('list');
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState(EMPTY_COLLECTION_BOY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [centres, setCentres] = useState([]);

  const [confirmTarget, setConfirmTarget] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4500);
  };

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await collectionBoyService.list({});
      if (res.success) setItems(res.data || []);
      else setLoadError(res.message || 'Could not load the collection boys.');
    } catch (err) {
      setLoadError(err.response?.data?.message || 'Could not load the collection boys.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  useEffect(() => {
    let cancelled = false;
    masterService.getCentres({})
      .then((res) => {
        if (cancelled || !res?.success) return;
        const list = Array.isArray(res.data) ? res.data : (res.data?.items || []);
        setCentres(list.filter((c) => c.isActive !== false));
      })
      .catch(() => { /* the centre list is optional: without it a collection boy simply works for every centre */ });
    return () => { cancelled = true; };
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const handleAddNew = () => {
    setFormData(EMPTY_COLLECTION_BOY);
    setIsEditMode(false);
    setErrors({});
    setViewMode('form');
  };

  const handleEdit = (row) => {
    setFormData(formFromRecord(row));
    setIsEditMode(true);
    setErrors({});
    setViewMode('form');
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (saving) return;
    const errs = validateCollectionBoy(formData);
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      showToast('Please correct the highlighted fields.', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = toPayload(formData);
      const res = isEditMode
        ? await collectionBoyService.update(formData.collectionBoyId, payload)
        : await collectionBoyService.create(payload);
      if (!res.success) {
        showToast(res.message || 'Could not save the collection boy.', 'error');
        return;
      }
      showToast(isEditMode ? 'Collection boy updated.' : `Collection boy added (${res.data?.boyCode || ''}).`);
      await fetchItems();
      setViewMode('list');
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not save the collection boy.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const executeStatusChange = async () => {
    if (!confirmTarget) return;
    setStatusUpdating(true);
    try {
      const newStatus = confirmTarget.action === 'activate';
      const res = await collectionBoyService.updateStatus(confirmTarget.id, newStatus);
      if (!res.success) { showToast(res.message || 'Could not change the status.', 'error'); return; }
      showToast(`Collection boy ${newStatus ? 'activated' : 'deactivated'}.`);
      setItems((prev) => prev.map((i) => (i.collectionBoyId === confirmTarget.id ? { ...i, isActive: newStatus } : i)));
      setConfirmTarget(null);
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not change the status.', 'error');
    } finally {
      setStatusUpdating(false);
    }
  };

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return items.filter((i) => {
      const matchesSearch = !q
        || (i.boyName || '').toLowerCase().includes(q)
        || (i.mobile || '').includes(q)
        || (i.boyCode || '').toLowerCase().includes(q);
      const matchesStatus = statusFilter === 'all' || (statusFilter === 'active' ? i.isActive : !i.isActive);
      return matchesSearch && matchesStatus;
    });
  }, [items, searchQuery, statusFilter]);

  const { page, pageSize, totalRecords, setPage, setPageSize, paginatedItems } = usePagination({
    items: filteredItems,
    initialPageSize: 25,
    resetDeps: [searchQuery, statusFilter],
  });

  const err = (k) => (errors[k] ? 'border-danger' : '');

  return (
    <MasterPageWrapper
      masterTitle={viewMode === 'form' ? (isEditMode ? 'Update Collection Boy' : 'Add Collection Boy') : 'Collection Boy Master'}
      masterName="Collection Boy"
      viewMode={viewMode}
      onToggleView={(mode) => { if (mode === 'form' && !isEditMode) handleAddNew(); else setViewMode(mode); }}
    >
      {toast && <div className={`cb-toast ${toast.type === 'error' ? 'error' : ''}`} role="alert">{toast.message}</div>}

      {viewMode === 'form' ? (
        <div className="glass-card fade-in" style={{ padding: '1.5rem' }}>
          <form onSubmit={handleSave} noValidate>
            <FormSection title="01. Identity & Contact" description="Who the person is and how to reach them. The name appears in the list on the Registration screen.">
              <div className="form-grid">
                {isEditMode && (
                  <Field label="Code"><input className="form-input" value={formData.boyCode} disabled readOnly /></Field>
                )}
                <Field label="Full Name" required error={errors.boyName}>
                  <input className={`form-input ${err('boyName')}`} maxLength={120} placeholder="e.g. Ramesh Kumar" value={formData.boyName} onChange={(e) => handleChange('boyName', e.target.value)} />
                </Field>
                <Field label="Mobile Number" required error={errors.mobile}>
                  <input className={`form-input ${err('mobile')}`} inputMode="numeric" maxLength={15} placeholder="10 digit mobile" value={formData.mobile} onChange={(e) => handleChange('mobile', e.target.value)} />
                </Field>
                <Field label="Alternate Mobile" error={errors.altMobile}>
                  <input className={`form-input ${err('altMobile')}`} inputMode="numeric" maxLength={15} value={formData.altMobile} onChange={(e) => handleChange('altMobile', e.target.value)} />
                </Field>
                <Field label="Email" error={errors.email}>
                  <input type="email" className={`form-input ${err('email')}`} maxLength={120} value={formData.email} onChange={(e) => handleChange('email', e.target.value)} />
                </Field>
                <Field label="Gender">
                  <select className="form-select" value={formData.gender} onChange={(e) => handleChange('gender', e.target.value)}>
                    <option value="">-- Select --</option>
                    {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
                  </select>
                </Field>
                <Field label="Date of Birth" error={errors.dateOfBirth}>
                  <input type="date" className={`form-input ${err('dateOfBirth')}`} value={formData.dateOfBirth} onChange={(e) => handleChange('dateOfBirth', e.target.value)} />
                </Field>
              </div>
            </FormSection>

            <FormSection title="02. Work Details" description="Where and how the person works">
              <div className="form-grid">
                <Field label="Works For">
                  <select className="form-select" value={formData.centreId} onChange={(e) => handleChange('centreId', e.target.value)}>
                    <option value="">All centres</option>
                    {centres.map((c) => <option key={c.centreId} value={c.centreId}>{c.centreName}</option>)}
                  </select>
                </Field>
                <Field label="Employment Type">
                  <select className="form-select" value={formData.employmentType} onChange={(e) => handleChange('employmentType', e.target.value)}>
                    {EMPLOYMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Joining Date" error={errors.joiningDate}>
                  <input type="date" className={`form-input ${err('joiningDate')}`} value={formData.joiningDate} onChange={(e) => handleChange('joiningDate', e.target.value)} />
                </Field>
                <Field label="Vehicle Number">
                  <input className="form-input" maxLength={20} placeholder="e.g. HR 26 AB 1234" value={formData.vehicleNo} onChange={(e) => handleChange('vehicleNo', e.target.value.toUpperCase())} />
                </Field>
                <Field label="Status">
                  <label className="cb-switch">
                    <input type="checkbox" checked={formData.isActive} onChange={(e) => handleChange('isActive', e.target.checked)} />
                    <span>{formData.isActive ? 'Active (shown on Registration)' : 'Inactive (hidden on Registration)'}</span>
                  </label>
                </Field>
              </div>
            </FormSection>

            <FormSection title="03. ID Proof" description="Government ID kept for the record">
              <div className="form-grid">
                <Field label="ID Proof Type" error={errors.idProofType}>
                  <select className={`form-select ${err('idProofType')}`} value={formData.idProofType} onChange={(e) => handleChange('idProofType', e.target.value)}>
                    <option value="">-- Select --</option>
                    {ID_PROOF_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="ID Number" error={errors.idProofNumber}>
                  <input className={`form-input ${err('idProofNumber')}`} maxLength={40} value={formData.idProofNumber} onChange={(e) => handleChange('idProofNumber', e.target.value)} />
                </Field>
              </div>
            </FormSection>

            <FormSection title="04. Address" description="Residential address">
              <div className="form-grid">
                <Field label="Address" wide>
                  <textarea className="form-input" rows={2} maxLength={300} placeholder="House / street / area" value={formData.address} onChange={(e) => handleChange('address', e.target.value)} />
                </Field>
                <Field label="City">
                  <input className="form-input" maxLength={80} value={formData.city} onChange={(e) => handleChange('city', e.target.value)} />
                </Field>
                <Field label="PIN Code" error={errors.pincode}>
                  <input className={`form-input ${err('pincode')}`} inputMode="numeric" maxLength={10} value={formData.pincode} onChange={(e) => handleChange('pincode', e.target.value)} />
                </Field>
                <Field label="Remarks" wide>
                  <textarea className="form-input" rows={2} maxLength={300} value={formData.remarks} onChange={(e) => handleChange('remarks', e.target.value)} />
                </Field>
              </div>
            </FormSection>

            <div className="form-actions-bar" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--border-color)' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setViewMode('list')} disabled={saving}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : isEditMode ? 'Update Collection Boy' : 'Add Collection Boy'}</button>
            </div>
          </form>
        </div>
      ) : (
        <div className="glass-card fade-in" style={{ padding: '1.5rem' }}>
          <div className="filter-bar">
            <div className="filter-bar-left">
              <input
                type="text"
                className="form-input"
                style={{ width: '320px', minWidth: '220px' }}
                placeholder="Search by name, mobile or code..."
                aria-label="Search collection boys"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
              />
              <select className="form-select" style={{ width: '160px' }} aria-label="Status filter" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                <option value="all">All Status</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>
            </div>
            <div className="filter-bar-right">
              <button type="button" className="btn btn-primary" onClick={handleAddNew}>+ Add Collection Boy</button>
            </div>
          </div>

          {loadError && <p className="error-message" role="alert" style={{ marginTop: '1rem' }}>{loadError}</p>}

          <div className="table-responsive" style={{ marginTop: '1rem', overflowX: 'auto' }}>
            <table className="master-table mvc-table" style={{ width: '100%', minWidth: '900px' }}>
              <thead>
                <tr>
                  <th style={{ width: '90px' }}>Code</th>
                  <th style={{ minWidth: '200px' }}>Name</th>
                  <th style={{ minWidth: '140px' }}>Mobile</th>
                  <th style={{ minWidth: '140px' }}>Works For</th>
                  <th style={{ width: '120px' }}>Type</th>
                  <th style={{ width: '110px' }}>Vehicle</th>
                  <th style={{ width: '100px', textAlign: 'center' }}>Status</th>
                  <th style={{ width: '170px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="text-center py-4 text-muted">Loading collection boys...</td></tr>
                ) : paginatedItems.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-4 text-muted">No collection boys found.</td></tr>
                ) : paginatedItems.map((b) => (
                  <tr key={b.collectionBoyId}>
                    <td><span className="badge badge-primary" style={{ fontFamily: 'monospace', fontWeight: 600 }}>{b.boyCode}</span></td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{b.boyName}</div>
                      {b.city && <div className="text-muted" style={{ fontSize: '11px' }}>{b.city}</div>}
                    </td>
                    <td>
                      <div>{b.mobile}</div>
                      {b.altMobile && <div className="text-muted" style={{ fontSize: '11px' }}>{b.altMobile}</div>}
                    </td>
                    <td>{b.centreName || 'All centres'}</td>
                    <td>{b.employmentType}</td>
                    <td>{b.vehicleNo || '—'}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={`badge ${b.isActive ? 'badge-success' : 'badge-danger'}`}>{b.isActive ? 'Active' : 'Inactive'}</span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => handleEdit(b)}>Edit</button>
                        <button
                          type="button"
                          className={`btn btn-sm ${b.isActive ? 'btn-outline-danger' : 'btn-outline-success'}`}
                          onClick={() => setConfirmTarget({ id: b.collectionBoyId, name: b.boyName, action: b.isActive ? 'deactivate' : 'activate' })}
                        >
                          {b.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <CommonPagination page={page} pageSize={pageSize} totalRecords={totalRecords} onPageChange={setPage} onPageSizeChange={setPageSize} pageSizeOptions={[15, 25, 50, 100]} />
        </div>
      )}

      <ConfirmModal
        isOpen={Boolean(confirmTarget)}
        title={`${confirmTarget?.action === 'activate' ? 'Activate' : 'Deactivate'} Collection Boy`}
        message={`Are you sure you want to ${confirmTarget?.action} "${confirmTarget?.name}"?${confirmTarget?.action === 'deactivate' ? ' The name will no longer be listed on the Registration screen.' : ''}`}
        confirmLabel={confirmTarget?.action === 'activate' ? 'Activate' : 'Deactivate'}
        variant={confirmTarget?.action === 'activate' ? 'primary' : 'danger'}
        onConfirm={executeStatusChange}
        onCancel={() => setConfirmTarget(null)}
        loading={statusUpdating}
      />
    </MasterPageWrapper>
  );
};

export default CollectionBoyMasterPage;
